import Vue from "vue";
import VueRouter from "vue-router";
import LoginLayout from '@/layouts/Login';
import PrincipalLayout from '@/layouts/Principal'
import { store } from '../store';
import { AUTH_ERRORS } from '@/service/auth/auth.service';
import { showErrorAlert, showWarningAlert } from '@/helpers/swalHelper';
import { onUnauthorized } from '@/http-commons';
import { AdminRouter } from "./adminRouter";
import { safeRedirect } from "./redirect";
Vue.use(VueRouter);
const routes = [
  {
    path: "/",
    component: LoginLayout,
    children: [
      {
        path: "",
        component: () => import( /*webpackChunkName: "Login"*/ '@/views/auth/Login.vue'),
        name: "login"
      }
      // { path: "register", component: () => import(/*webpackChunkName: "Register"*/ "@/views/auth/Register.vue"), name: "register" }
    ]
  },
  {
    path: '/controlPanel',
    component: PrincipalLayout,
    children: [
      ...AdminRouter,
      {
        path: 'unauthorized',
        component: () => import('@/views/shared/Unauthorized.vue'),
        name: 'Unauthorized',
        // Sin `requiresAuth`: esta pantalla es la respuesta a un rol
        // insuficiente. Si exigiera autenticacion, el propio guard
        // reenviaria a 'Unauthorized' en bucle.
        meta: { requiresAuth: false }
      },
      {
        path: '*',
        component: () => import('@/views/shared/PageNotFound.vue'),
        meta: { requiresAuth: false }
      }
    ]
  },
  {
    // Catch-all global. Sin esto, cualquier ruta que no cuelgue de
    // '/controlPanel' (por ejemplo '/ventas' en vez de '/controlPanel/ventas')
    // no casa con ninguna ruta y la SPA se queda en blanco.
    // Va fuera de '/controlPanel' a proposito: el catch-all de ese bloque
    // solo cubre lo que cuelga de '/controlPanel'.
    // Cuelga de LoginLayout porque es quien aporta el `v-app` que Vuetify
    // necesita; sin el, PageNotFound se pintaba sin tema ni estilos.
    path: '*',
    component: LoginLayout,
    children: [
      {
        path: '',
        component: () => import('@/views/shared/PageNotFound.vue'),
        meta: { requiresAuth: false }
      }
    ]
  }
];


const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

const notifySessionEnded = () => showWarningAlert({
  title: 'Tu sesión terminó',
  text: 'Inicia sesión de nuevo para continuar.'
});

// Un 401 de cualquier peticion (token vencido o revocado a mitad de uso). Cierra
// la sesion y lleva al login recordando la pantalla actual, sin recargar la
// pagina. Varias peticiones de una misma pantalla pueden fallar a la vez: solo
// la primera actua.
let expiringSession = false;
export const expireSession = async () => {
  const current = router.currentRoute;
  if (expiringSession || current.name === 'login') {
    return;
  }
  expiringSession = true;
  try {
    await store.dispatch('auth/logout');
    notifySessionEnded();
    await router.replace({ name: 'login', query: { redirect: current.fullPath } });
  } catch (error) {
    // Navegacion cancelada o duplicada: ya se esta yendo al login.
  } finally {
    expiringSession = false;
  }
};

onUnauthorized(expireSession);

export const authGuard = async (to, from, next) => {
  // Con sesion iniciada no tiene sentido ver el login: se va al destino
  // pendiente (o al panel). Si el token resulta invalido, el guard de abajo
  // cierra la sesion y regresa aqui, ya sin `loggedIn`, asi que no hay bucle.
  if (to.name === 'login' && store.state.auth.status.loggedIn) {
    return next(safeRedirect(to.query.redirect));
  }

  if (!to.matched.some(record => record.meta.requiresAuth)) {
    return next();
  }

  try {
    const user = await store.dispatch('auth/validateToken');

    // `to.meta.roles` solo existe si la ruta lo declara. Sin esta guarda,
    // una ruta con `requiresAuth` pero sin `roles` reventaba el guard con
    // "Cannot read property 'some' of undefined" y expulsaba al login.
    const allowedRoles = to.meta.roles;
    const hasRole = Array.isArray(allowedRoles)
      ? allowedRoles.some(role => user?.roles?.includes(role))
      : true;

    if (user && user.roles && hasRole){
      next();
    } else {
      next({ name: 'Unauthorized' });
    }
  } catch (error) {
    if (error && error.code === AUTH_ERRORS.NETWORK) {
      // No se pudo verificar la sesion (red caida o servidor con error): eso
      // no significa que sea invalida, asi que no se expulsa al usuario.
      // Si ya estaba en una pantalla se queda en ella y puede reintentar; en
      // la carga inicial no hay pantalla a la que volver, asi que se deja
      // pasar: el servidor sigue validando cada peticion.
      showErrorAlert({
        title: 'No se pudo verificar la sesión',
        text: 'Revisa tu conexión e inténtalo de nuevo.'
      });
      return from.matched.length ? next(false) : next();
    }

    // Sin token, token rechazado o error inesperado: la sesion no sirve.
    // Se cierra de verdad (localStorage y store) antes de ir al login; si no,
    // el login veria `loggedIn` en true y devolveria al usuario al panel.
    await store.dispatch('auth/logout');
    if (error && error.code === AUTH_ERRORS.INVALID) {
      notifySessionEnded();
    }
    next({ name: 'login', query: { redirect: to.fullPath } });
  }
};

router.beforeEach(authGuard);

export default router;
